import { useState, KeyboardEvent } from 'react';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Tag, Plus, X } from 'lucide-react';
import { cn } from '@/lib/utils';

interface TagSelectorProps {
  selectedTags: string[];
  onChange: (tags: string[]) => void;
  maxTags?: number;
}

const PREDEFINED_TAGS = {
  themes: [
    'Coming of Age',
    'Family',
    'Friendship',
    'Love',
    'Loss',
    'Identity',
    'Power',
    'Justice',
    'Survival',
    'Redemption',
    'Revenge',
    'War',
    'Politics',
    'Religion',
    'Class',
    'Race',
    'Gender',
    'Nature',
  ],
  moods: [
    'Dark',
    'Light',
    'Emotional',
    'Funny',
    'Tense',
    'Cozy',
    'Inspiring',
    'Heartbreaking',
    'Atmospheric',
    'Fast-paced',
    'Slow-burn',
    'Twisty',
    'Thought-provoking',
    'Escapist',
    'Gritty',
    'Whimsical',
  ],
};

export function TagSelector({ selectedTags, onChange, maxTags = 999 }: TagSelectorProps) {
  const [inputValue, setInputValue] = useState('');

  const toggleTag = (tag: string) => {
    if (selectedTags.includes(tag)) {
      onChange(selectedTags.filter(t => t !== tag));
    } else if (selectedTags.length < maxTags) {
      onChange([...selectedTags, tag]);
    }
  };

  const handleAddCustomTag = () => {
    const tag = inputValue.trim();
    if (tag && !selectedTags.includes(tag) && selectedTags.length < maxTags) {
      onChange([...selectedTags, tag]);
      setInputValue('');
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddCustomTag();
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <Label className="text-sm font-medium mb-2 block">
          Themes & Moods (Optional)
        </Label>
        <p className="text-xs text-muted-foreground mb-3">
          Help readers find books with similar vibes
        </p>
      </div>

      {/* Selected tags with remove option */}
      {selectedTags.length > 0 && (
        <div className="flex flex-wrap gap-2 pb-2 border-b">
          {selectedTags.map((tag) => (
            <Badge key={tag} variant="default" className="pl-3 pr-2 py-1">
              {tag}
              <button
                type="button"
                onClick={() => onChange(selectedTags.filter(t => t !== tag))}
                className="ml-1.5 hover:opacity-70"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}

      <div className="space-y-3">
        <div>
          <h4 className="text-xs font-medium text-muted-foreground mb-2">Themes</h4>
          <div className="flex flex-wrap gap-2">
            {PREDEFINED_TAGS.themes.map((tag) => {
              const isSelected = selectedTags.includes(tag);
              const isDisabled = !isSelected && selectedTags.length >= maxTags;
              
              return (
                <Badge
                  key={tag}
                  variant={isSelected ? 'default' : 'outline'}
                  className={cn(
                    'cursor-pointer transition-all',
                    isDisabled && 'opacity-50 cursor-not-allowed'
                  )}
                  onClick={() => !isDisabled && toggleTag(tag)}
                >
                  {tag}
                </Badge>
              );
            })}
          </div>
        </div>

        <div>
          <h4 className="text-xs font-medium text-muted-foreground mb-2">Moods</h4>
          <div className="flex flex-wrap gap-2">
            {PREDEFINED_TAGS.moods.map((tag) => {
              const isSelected = selectedTags.includes(tag);
              const isDisabled = !isSelected && selectedTags.length >= maxTags;
              
              return (
                <Badge
                  key={tag}
                  variant={isSelected ? 'default' : 'outline'}
                  className={cn(
                    'cursor-pointer transition-all',
                    isDisabled && 'opacity-50 cursor-not-allowed'
                  )}
                  onClick={() => !isDisabled && toggleTag(tag)}
                >
                  {tag}
                </Badge>
              );
            })}
          </div>
        </div>
      </div>

      {/* Custom tag input */}
      <div className="pt-2">
        <h4 className="text-xs font-medium text-muted-foreground mb-2">
          Add Your Own
        </h4>
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Tag className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Type a custom tag..."
              disabled={selectedTags.length >= maxTags}
              className="pl-10"
            />
          </div>
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={handleAddCustomTag}
            disabled={!inputValue.trim() || selectedTags.length >= maxTags}
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>
        <p className="text-xs text-muted-foreground mt-1">
          Press Enter to add • {selectedTags.length} selected
        </p>
      </div>
    </div>
  );
}
